// Service registry: single source of truth for nav, hubs, service pages,
// footer, and the homepage service grid (DECISIONS D-001).
//
// Add a service by adding an entry here, plus a route line in
// scripts/routes.js and an seo.config.js entry. No new page code.
//
// `primaryNav: true` groups surface in the main header; the rest live under
// "More" to keep the nav uncluttered (site-architecture.md §1).

export const serviceGroups = [
  {
    slug: "skilled-migration",
    title: "Skilled Migration",
    primaryNav: true,
    blurb:
      "Points-tested permanent and provisional visas for skilled workers, plus skills assessment.",
    services: [
      {
        slug: "skilled-independent-189",
        title: "Skilled Independent (189)",
        forWho: "Skilled workers with no sponsor, invited via SkillSelect.",
      },
      {
        slug: "skilled-nominated-190",
        title: "Skilled Nominated (190)",
        forWho: "Skilled workers nominated by a state or territory.",
      },
      {
        slug: "skilled-regional-491",
        title: "Skilled Work Regional (491)",
        forWho: "Provisional regional visa, a pathway to the 191.",
      },
      {
        slug: "permanent-regional-191",
        title: "Permanent Residence Regional (191)",
        forWho: "PR after meeting the regional 491 requirements.",
      },
      {
        slug: "skills-assessment",
        title: "Skills Assessment",
        forWho: "The mandatory first step for most skilled visas.",
      },
    ],
  },
  {
    slug: "employer-sponsored",
    title: "Employer Sponsored",
    primaryNav: true,
    blurb:
      "Visas where an approved Australian employer sponsors a skilled worker.",
    services: [
      {
        slug: "skills-in-demand-482",
        title: "Skills in Demand (482)",
        forWho: "Employer-sponsored skilled work visa (replaced the TSS).",
      },
      {
        slug: "employer-nomination-186",
        title: "Employer Nomination Scheme (186)",
        forWho: "Permanent residence through employer nomination.",
      },
      {
        slug: "regional-494",
        title: "Skilled Employer Regional (494)",
        forWho: "Regional employer-sponsored provisional visa.",
      },
    ],
  },
  {
    slug: "family-partner",
    title: "Family & Partner",
    primaryNav: true,
    blurb:
      "Support to prepare and get matched for partner, parent, and child visa pathways.",
    services: [
      {
        slug: "partner-visa",
        title: "Partner Visa (820/801, 309/100)",
        forWho: "For partners and spouses of Australian citizens or PRs.",
      },
      {
        slug: "prospective-marriage-300",
        title: "Prospective Marriage (300)",
        forWho: "For fiances intending to marry in Australia.",
      },
      {
        slug: "parent-visa",
        title: "Parent Visa",
        forWho: "For parents of an eligible Australian child.",
      },
      {
        slug: "child-visa",
        title: "Child Visa",
        forWho: "For dependent children of Australian parents.",
      },
    ],
  },
  {
    slug: "student-graduate",
    title: "Student & Graduate",
    primaryNav: true,
    blurb:
      "Study in Australia, extend your stay, and progress to a graduate visa.",
    services: [
      {
        slug: "student-500",
        title: "Student Visa (500)",
        forWho: "For international students enrolled in a registered course.",
      },
      {
        slug: "student-visa-extension",
        title: "Student Visa Extension",
        forWho: "For students continuing or changing their studies.",
      },
      {
        slug: "change-of-course",
        title: "Change of Course or Institution",
        forWho: "Switching course or provider while on a student visa.",
      },
      {
        slug: "graduate-485",
        title: "Graduate Visa (485)",
        forWho: "Temporary work rights after graduating in Australia.",
      },
      {
        slug: "admissions",
        title: "Admissions",
        forWho: "Course and university placement support.",
      },
      {
        slug: "oshc",
        title: "Overseas Student Health Cover",
        forWho: "Health cover required for the student visa.",
      },
    ],
  },
  {
    slug: "visitor-working-holiday",
    title: "Visitor & Working Holiday",
    primaryNav: false,
    blurb: "Short-stay visitor, sponsored family, and working holiday visas.",
    services: [
      {
        slug: "visitor-600",
        title: "Visitor Visa (600)",
        forWho: "For tourism, family visits, or business visitors.",
      },
      {
        slug: "sponsored-family-visitor",
        title: "Sponsored Family Visitor",
        forWho: "Visitor visa sponsored by an Australian relative.",
      },
      {
        slug: "working-holiday",
        title: "Working Holiday (417/462)",
        forWho: "For young travellers wanting to work and holiday.",
      },
    ],
  },
  {
    slug: "business-investment",
    title: "Business & Investment",
    primaryNav: false,
    blurb:
      "Preparation and matching support for business and investment migration pathways.",
    services: [
      {
        slug: "business-investment-advisory",
        title: "Business & Investment Support",
        forWho: "Documentation and matching for business and investment pathways.",
      },
    ],
  },
  {
    slug: "review-appeals",
    title: "Review & Appeals",
    primaryNav: false,
    blurb:
      "Urgent support after a visa refusal or cancellation, matched with a registered migration agent for the review.",
    services: [
      {
        slug: "visa-refusals",
        title: "Visa Refusals",
        forWho:
          "After a refusal, matched fast with a registered migration agent who handles the review.",
      },
      {
        slug: "visa-cancellations",
        title: "Visa Cancellations",
        forWho:
          "Responding to a cancelled visa, with the review handled by a registered migration agent.",
      },
    ],
  },
];

// Helpers
export const primaryGroups = serviceGroups.filter((g) => g.primaryNav);
export const moreGroups = serviceGroups.filter((g) => !g.primaryNav);
export const groupPath = (g) => `/${g.slug}/`;
export const servicePath = (g, s) => `/${g.slug}/${s.slug}/`;
export const findGroup = (slug) => serviceGroups.find((g) => g.slug === slug);

export const findService = (groupSlug, serviceSlug) => {
  const g = findGroup(groupSlug);
  if (!g) return null;
  const service = g.services.find((s) => s.slug === serviceSlug);
  return service ? { group: g, service } : null;
};

// Find a service by its slug alone (used for cross-sell links).
export const findServiceAnywhere = (serviceSlug) => {
  for (const g of serviceGroups) {
    const service = g.services.find((s) => s.slug === serviceSlug);
    if (service) return { group: g, service };
  }
  return null;
};

export default serviceGroups;
